import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { CacheService } from 'projects/common/src/app/services/cache.service';
import { catchError, map } from 'rxjs/operators';
import { IOutputFilter } from '../../core/interfaces/output-filter';
import { CommunicationService } from 'src/app/services/communication';
import { Logger } from 'projects/common/src/app/classes/logger';
import { IDrillingReport, IDrillGrouped, ITotalByMachine } from '../interfaces/report-drilling';
import { DatePresentationPipe } from '../../core/pipes/date-presentation.pipe';
import { UrlBuilderService } from 'projects/communication/src/app/services/url-builder.service';

@Injectable({ providedIn: 'root' })
export class ReportDrillingService extends Logger {
  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private communicationService: CommunicationService,
    private datePipe: DatePresentationPipe,
    private datePipeHCM: DatePresentationPipe,
    private urlBuilder: UrlBuilderService,
  ) {
    super();
  }

  /**
   * Get drilling status machine data
   */
  public GetDrillingReportData(selectedFilter: IOutputFilter): Promise<any> {
    const params = this.setParams(selectedFilter);
    const url = `${this.urlBuilder.apiUrl}/Report/GetDrillReport`;
    return this.communicationService
      .get(url, params)
      .pipe(
        map((report: IDrillingReport) => {
          report.drillGrouped = this.drillsGroupedMapper(report.drillGrouped);
          report.totalByMachine = this.totalByMachineMapper(report.totalByMachine);
          return report;
        }),
        catchError((err) => this.handleError(err)),
      )
      .toPromise();
  }

  /**
   * Get drilling HCM status machine data
   */
  public getDataDrillHCM(selectedFilter: IOutputFilter): Promise<any> {
    const params = this.setParams(selectedFilter);
    const url = `${this.urlBuilder.apiUrl}/Report/GetDrillReportHCM`;
    return this.communicationService
      .get(url, params)
      .pipe(
        map((report: IDrillingReport) => {
          report.drillGrouped = this.drillsHCMGroupedMapper(report.drillGrouped);
          report.totalByMachine = this.totalByMachineMapper(report.totalByMachine);
          return report;
        }),
        catchError((err) => this.handleError(err)),
      )
      .toPromise();
  }

  /**
   * Function to map drills for rigth order in table
   *
   * @param drillGrouped drills to map
   */
  private drillsGroupedMapper(drillGrouped: IDrillGrouped[]): any[] {
    return drillGrouped.map((value) => {
      return {
        machineId: value.machineId,
        machineName: value.machineName,
        date: this.datePipe.transform(value.date),
        shiftId: value.shiftId,
        shiftName: value.shiftName,
        ZoneName: value.zoneName,
        countDisplacementNumber: value.countDisplacementNumber,
        depth: value.depth,
        displacementNumber: value.displacementNumber,
        operatorName: value.operatorName,
      };
    });
  }

  /**
   * Function to map drills for rigth order in table
   *
   * @param drillGrouped drills to map
   */
  private drillsHCMGroupedMapper(drillGrouped: IDrillGrouped[]): any[] {
    return drillGrouped.map((value) => {
      return {
        machineId: value.machineId,
        machineName: value.machineName,
        date: this.datePipeHCM.transform(value.date),
        shiftId: value.shiftId,
        shiftName: value.shiftName,
        drillClassificationName: value.drillClassificationName,
        countDisplacementNumber: value.countDisplacementNumber,
        depth: value.depth,
        displacementNumber: value.displacementNumber,
        statusName: value.statusName,
      };
    });
  }

  /**
   * Function to map total drills by machine for rigth order in table
   *
   * @param totalByMachine data to map
   */
  private totalByMachineMapper(totalByMachine: ITotalByMachine[]): any[] {
    return totalByMachine.map((value) => {
      return {
        machineName: value.machineName,
        totalCountDisplacementNumber: value.totalCountDisplacementNumber,
        totalDepth: value.totalDepth,
      };
    });
  }

  /**
   * Function to set params and send selected filters to request
   *
   * @param selectedFilters Filters selected to save params
   */
  private setParams(selectedFilters: IOutputFilter): any {
    const companyId = this.cacheService.local('session').user.company.id;

    return {
      startDate: selectedFilters.startDate,
      endDate: selectedFilters.endDate,
      shiftId: String(selectedFilters.shiftId),
      machineList: selectedFilters.machineId,
      machineTypeList: selectedFilters.machineTypeId,
      companyId,
    };
  }

  /**
   * Get excel from GetChecklistReportDetailDocument endpoint
   *
   * @param startDate startDate
   * @param endDate endDate
   * @param shiftId shiftId
   * @param machineTypeId machineTypeId
   * @param machineList machineList
   */
  public getDrillingReportExcel(params: any): Observable<any> {
    const companyId = this.cacheService.local('session').user.company.id;
    let url = `${this.urlBuilder.apiUrl}/Report/GetDrillReportDocument?`;
    url += `startDate=${params.startDate}`;
    url += `&endDate=${params.endDate}`;
    url += `&shiftId=${params.shiftId}`;
    url += `&machineList=${params.machineList}`;
    url += `&machineTypeList=${params.machineType}`;
    url += `&companyId=${companyId}`;
    return this.http.request('GET', url, { responseType: 'blob' }).pipe(catchError((error) => throwError(() => error.error)));
  }

  /**
   * Get excel from GetChecklistReportDetailDocument endpoint
   *
   * @param startDate startDate
   * @param endDate endDate
   * @param shiftId shiftId
   * @param machineTypeId machineTypeId
   * @param machineList machineList
   */
  public getDrilliHCMReportExcel(params: any): Observable<any> {
    const companyId = this.cacheService.local('session').user.company.id;
    let url = `${this.urlBuilder.apiUrl}/Report/GetDrillReportHCMDocument?`;
    url += `startDate=${params.startDate}`;
    url += `&endDate=${params.endDate}`;
    url += `&shiftId=${params.shiftId}`;
    url += `&companyId=${companyId}`;
    return this.http.request('GET', url, { responseType: 'blob' }).pipe(catchError((error) => throwError(() => error.error)));
  }

  /**
   * Handle error
   *
   * @param err error
   */
  private handleError(err: any): Observable<any> {
    this.error(err);
    return throwError(err);
  }
}
